import Promise from 'bluebird';
import _ from 'underscore';
import $ from 'jquery';
import { segmentationType } from '@bingads-webui-campaign/segmentation-utilities';
import { stringifySync } from '@bingads-webui/safe-json';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import { CAMPAIGN_TYPES } from '@bingads-webui-campaign/competition-auction-insight-preferences';
import { MAX_NUMBER_OF_ACCOUNT } from '@bingads-webui-campaign/auction-insight-common';

import { extractFilter, filterData } from './filter';
import { entitySchema } from './schema';
import { getFilteredEntitiesAndUpdateOptions } from './get-filtered-entities';
import { handleUnauthorizedResponse, isCardDataEnabled } from './util';

const API = '/AdInsight/V2/Customers({cid})/Accounts({aid})/Default.GetAuctionInsightDataV2';
const APIForMCC = '/AdInsight/V2/Customers({cid})/MccAuctionInsight.GetMccAuctionInsightData';
const APIForHourly = '/AdInsight/V2/Customers({cid})/Accounts({aid})/Default.GetAuctionInsightHourlyData';
const APIForMCCHourly = 'AdInsight/V2/Customers({cid})/MccAuctionInsight.GetMccAuctionInsightHourlyData';

// from mm/dd/yyyy to yyyymmdd from mm/dd/yyyy to yyyy-mm-dd
function convertDateStr(datetimeStr, seperator = '') {
  const arr = datetimeStr.split('/');
  if (arr.length === 1) {
    return datetimeStr;
  }
  if (arr[0].length === 1) {
    arr[0] = `0${arr[0]}`;
  }
  if (arr[1].length === 1) {
    arr[1] = `0${arr[1]}`;
  }

  arr.unshift(arr.pop());
  return arr.join(seperator);
}

function normalizeSegment(segment, rawSegmentType) {
  const timeMap = {
    Day: 'Day',
    Week: 'Week',
    Month: 'Month',
    Quarter: 'Quarter',
    Year: 'Year',
  };
  const StringValue = rawSegmentType in timeMap ? convertDateStr(segment, '-') : segment;

  return {
    SegmentationType: rawSegmentType,
    StringValue,
  };
}

export function normalize(raw, i18n, isCustomerLevel) {
  const data = _.map(raw.Entries, (entry, itemIndex) => {
    const item = {};
    item.UsedImpressions = raw.UsedImpressions;
    item.DisplayDomain = entry.DisplayDomain;

    if (isCustomerLevel) {
      item.HasInsight = itemIndex !== 0 && entry.DisplayDomain === 'You' && !_.isEmpty(raw.UnderPerformingAccountIds);
      item.ItemIndex = itemIndex;
    }

    item.LocalizedDisplayDomain = entry.DisplayDomain === 'You' && i18n ?
      i18n.getString('AuctionInsights_You') :
      entry.DisplayDomain;
    item.PerformanceMetrics = _.omit(entry.AggregatedKpi, 'Segments');

    item.PerformanceMetrics.SegmentedData = _.map(entry.SegmentedKpi, (seg) => {
      const segment = {};
      segment.Data = _.omit(seg, 'Segments');
      segment.Key = _.map(
        seg.Segments,
        (segmentValue, index) => {
          const rawSegmentType = _.isArray(raw.SegmentBy) ? raw.SegmentBy[index] : raw.SegmentBy;
          return normalizeSegment(segmentValue, rawSegmentType);
        }
      );
      return segment;
    });
    return item;
  });

  data.totalCount = _.size(raw.Entries);
  return data;
}

function sort(data, field, direction = 'ASC') {
  let ret;
  if (field === 'LocalizedDisplayDomain') {
    ret = _.sortBy(data, i => _.result(i, 'LocalizedDisplayDomain').toLowerCase());
  } else {
    ret = _.sortBy(data, i => _.result(_.result(i, 'PerformanceMetrics'), field));
  }

  return direction === 'ASC' ? ret : ret.reverse();
}

function formatUrl(url, scope) {
  return url.replace('{aid}', scope.accountId).replace('{cid}', scope.customerId);
}

export class AuctionInsightService {
  constructor({
    scope,
    showAlertHandler,
    i18n,
    odataPath,
    currentScenario,
    currentActivity,
    campaignType,
  }) {
    this.scope = scope;
    this.i18n = i18n;
    this.showAlertHandler = showAlertHandler;
    this.cache = {};
    this.currentScenario = currentScenario;
    this.currentActivity = currentActivity;
    this.odataPath = odataPath;
    this.campaignType = campaignType;
    this.isCustomerLevel = this.scope.levelAt === levelAtConstant.CUSTOMER;

    if (this.isCustomerLevel && this.scope.accountsInfo.totalCount > MAX_NUMBER_OF_ACCOUNT) {
      currentActivity.trace(
        `Accounts more than ${MAX_NUMBER_OF_ACCOUNT}. Customer ID: ${this.scope.customerId}, accounts count: ${this.scope.accountsInfo.error || this.scope.accountsInfo.totalCount}`,
        'AuctionInsightService'
      );
    }
  }

  getUsedImpressions() {
    return this.cacheUsedImpressions || 0;
  }

  getNormalizedData() {
    return this.rawData ? normalize(this.rawData) : [];
  }

  findAll(op, { afterFindAll } = {}) {
    this.currentActivity.trace('findAll is called', 'AuctionInsightService/findAll');

    const option = this.generateRequestOption(op);
    const { request } = JSON.parse(option.data);
    if (request.LevelAt === levelAtConstant.KEYWORD &&
    this.campaignType === CAMPAIGN_TYPES.Shopping) {
      // MT doesn't support keyword level shopping campaign request and will return non-200.
      // In order to support this scenario in UI, fake the empty response.
      this.currentActivity.trace('send empty data for keyword level shopping campaign request', 'AuctionInsightService/findAll');
      this.rawData = null;
      return Promise.resolve([]);
    }

    if (this.isCustomerLevel &&
      (_.isEmpty(request.AccountIds) || request.AccountIds.length > MAX_NUMBER_OF_ACCOUNT)) {
      this.rawData = null;
      this.cacheUsedImpressions = 0;
      return Promise.resolve([]);
    }

    const cacheKey = `auction_key_${option.data}_${option.url}`;

    let useCache = false;
    if (!_.has(this.cache, cacheKey)) {
      // Only cache the raw data
      this.cache[cacheKey] = new Promise((resolve, reject) => {
        $.ajax(option).then((rawData) => {
          this.cacheUsedImpressions = rawData.UsedImpressions;
          resolve(rawData);
        }, reject);
      });
    } else {
      useCache = true;
    }
    this.currentActivity.trace(
      `${useCache ? 'hit cache, will not send request' : 'miss cache, send real request'}`,
      'AuctionInsightService/findAll'
    );

    let findAllActivity;
    if (!useCache) {
      findAllActivity = this.currentScenario.findAll.create();
      findAllActivity.start();
    }
    return this.cache[cacheKey].then(
      (rawData) => {
        if (!useCache) {
          findAllActivity.stop();
          if (afterFindAll) {
            afterFindAll({ rawData, request });
          }
        }
        this.scope.underPerformingAccountIds = rawData.UnderPerformingAccountIds;
        this.rawData = rawData;
        return this.getDataByOp(op, rawData);
      },
      (err) => {
        const errorDetails = `ErrorCode=${err.status}, Error=${stringifySync(err)}, Request=${stringifySync(option.data)}`;

        if (err.status === 0) {
          this.currentActivity.trace(`Request not able to be sent, ${errorDetails}`, 'AuctionInsightService/findAll');
        } else if (err.status === 401) {
          this.currentActivity.trace(`error in findAll, ${errorDetails}`, 'AuctionInsightService/findAll');
          handleUnauthorizedResponse(err);
        } else {
          this.currentActivity.error(`error in findAll, ${errorDetails}`, 'AuctionInsightService/findAll');
        }

        if (_.isFunction(this.showAlertHandler)) {
          this.showAlertHandler({
            message: this.i18n ? this.i18n.getString('GenericErrorMessage') : '',
            level: 'Error',
            dismissible: true,
          });
        } else {
          throw err;
        }

        return [];
      }
    );
  }

  generateRequestOption(op) {
    const query = _.result(op, 'query', {});
    const data = {
      request: getFilteredEntitiesAndUpdateOptions(op, this.scope),
    };

    this.currentActivity.trace(
      `request entityIds length: ${this.isCustomerLevel ? data.request.AccountIds.length : data.request.EntityIds.length}`,
      'AuctionInsightService/generateRequestOption'
    );

    // has segment
    const segmentTypes = _.isArray(query.SegmentationTypes) ?
      query.SegmentationTypes :
      [query.SegmentationTypes];

    const option = {
      contentType: 'application/json',
      type: 'POST',
      headers: {
        authorization: `CCMTSmallToken ${this.scope.token}`, // add prefix
      },
    };

    const formattedSegmentTypes = _.compact(segmentTypes);
    this.currentActivity.trace(
      `request segment type: ${_.isEmpty(formattedSegmentTypes) ? 'None' : formattedSegmentTypes}`,
      'AuctionInsightService/generateRequestOption'
    );

    const isSegmentHourOfDay = _.contains(formattedSegmentTypes, segmentationType.HourOfDay);
    if (isSegmentHourOfDay) {
      option.url = formatUrl(`${this.odataPath}${this.isCustomerLevel ? APIForMCCHourly : APIForHourly}`, this.scope);
      // Only segment by HourOfTheDay
      data.request.SegmentBy = segmentationType.HourOfDay;
    } else {
      option.url = formatUrl(`${this.odataPath}${this.isCustomerLevel ? APIForMCC : API}`, this.scope);
      if (!_.isEmpty(formattedSegmentTypes)) {
        data.request.SegmentBy = formattedSegmentTypes;
      }
    }

    data.request.StartDate = convertDateStr(query.startDate);
    data.request.EndDate = convertDateStr(query.endDate);

    if (this.isCustomerLevel) {
      data.request.CardDataEnabled = isCardDataEnabled(query);
    }

    this.currentActivity.trace(
      `request campaign type: '${this.campaignType ? '' : this.campaignType}'`,
      'AuctionInsightService/generateRequestOption'
    );
    if (this.campaignType) {
      data.request.CampaignType = this.campaignType;
    }
    option.data = JSON.stringify(data);

    return option;
  }

  getDataByOp(op, rawData) {
    let data = normalize(rawData, this.i18n, this.isCustomerLevel);

    if (!_.isEmpty(op.orderBy) && _.isArray(op.orderBy)) {
      const field = op.orderBy[0][0];
      const direction = op.orderBy[0][1];
      data = sort(data, field, direction);
      this.currentActivity.trace(
        `request sort field ${field} direction ${direction}`,
        'AuctionInsightService/getDataByOp'
      );
    }

    if (!_.isEmpty(op.where)) {
      const queries = _.map(extractFilter(op.where), (q) => {
        this.currentActivity.trace(
          `iterate request filter: query ${stringifySync(q)}`,
          'AuctionInsightService/getDataByOp'
        );
        const query = _.clone(q);
        if (_.result(entitySchema.properties[query.columnName], 'isPercentValue')) {
          // convert percent to decimal without rounding artifacts
          query.value = Math.round(q.value * 1e2) / 1e4;
        }
        return query;
      });

      data = filterData(queries, data);
    }

    data.totalCount = data.length;
    return data;
  }
}
